import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Subject, takeUntil, catchError, finalize, of } from 'rxjs';
import { IdpContractsApiService } from '../../../services/idp-contracts-api/idp-contracts-api.service';
import { IdpMeteringAggregateService } from '../../../services/idp-metering-aggregate/idp-metering-aggregate.service';
import {
  DashboardState,
  DashboardData,
  Contract,
  ContractPeriod,
  IdpContract
} from '../../../models/idpContracts';
import { IdpAggregateMeteringItem } from '../../../models/idpMeteringAggregate';
import { retry } from 'rxjs';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ContractUsagesComponent } from '../components/contract-usages/contract-usages.component';
import { Card } from 'primeng/card';
import { ProgressSpinner } from 'primeng/progressspinner';
import { MonthlyScansComponent } from '../components/monthly-scans/monthly-scans.component';
import { Menu } from 'primeng/menu';
import { PrimeTemplate } from 'primeng/api';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-intelligence-dashboard',
  templateUrl: './intelligence-dashboard.component.html',
  styleUrls: ['./intelligence-dashboard.component.scss'],
  imports: [
    Bind,
    Button,
    Tooltip,
    ContractUsagesComponent,
    Card,
    ProgressSpinner,
    MonthlyScansComponent,
    Menu,
    PrimeTemplate,
    NgClass,
    TranslatePipe
  ]
})
export class IntelligenceDashboardComponent implements OnInit, OnDestroy {
  private contractsApiService = inject(IdpContractsApiService);
  private idpMeteringAggregateService = inject(IdpMeteringAggregateService);

  private readonly destroy$ = new Subject<void>();
  private readonly initialState: DashboardState = {
    isLoading: false,
    isEmpty: false,
    isMeterDataLoading: false,
    isMeterDataEmpty: false,
    aggregateDataloading: false,
    pagesExtractedTotal: 0,
    yearlyPageExtractionQuota: 0,
    meterGrouptotalPercent: 0,
    selectedContractRange: '',
    selectedPeriodLabel: '',
    hasExceededQuota: false
  };

  state: DashboardState = { ...this.initialState };
  data: DashboardData = {
    contracts: [],
    menuItems: [],
    selectedPeriod: null,
    aggregateData: null
  };

  /**
   *
   */
  get meterColor(): string {
    return this.state.hasExceededQuota ? 'var(--p-red-500)' : 'var(--p-sd-primary-blue-500)';
  }

  /**
   *
   */
  ngOnInit(): void {
    this.loadContracts();
  }

  /**
   *
   */
  private loadContracts(): void {
    this.state.isMeterDataLoading = true;

    this.contractsApiService
      .getContracts()
      .pipe(
        retry({
          count: 2,
          delay: (error, retryCount) => {
            if (error.status === 0) {
              // Only retry on network errors
              return of(1000); // Delay 1 second before retry
            }
            throw error; // Do not retry on other errors
          },
          resetOnSuccess: true
        }),
        takeUntil(this.destroy$),
        catchError(error => {
          this.handleError();
          throw error;
        }),
        finalize(() => {
          this.state.isMeterDataLoading = false;
        })
      )
      .subscribe(contracts => this.handleContractsResponse(contracts));
  }

  /**
   *
   * @param contracts
   */
  private handleContractsResponse(contracts: IdpContract[]): void {
    this.data.contracts = this.mapContracts(contracts);

    if (!this.data.contracts.length) {
      this.state.isMeterDataEmpty = true;
      return;
    }

    // Collect all "Current" periods
    const currentPeriods: { contract: Contract; period: ContractPeriod }[] = [];

    this.data.contracts.forEach(contract => {
      contract.contractYears.forEach(period => {
        if (period.displayNameContractYearWise.includes('Current')) {
          currentPeriods.push({ contract, period });
        }
      });
    });

    // If multiple "Current" periods found, pick the last one
    if (currentPeriods.length > 0) {
      const lastCurrent = currentPeriods[currentPeriods.length - 1];
      this.setInitialPeriod(lastCurrent.contract, lastCurrent.period);
      return;
    }

    // If no "Current" periods found, set the last contract's last period as default
    const lastContract = this.data.contracts[this.data.contracts.length - 1];
    const lastPeriod = lastContract.contractYears[lastContract.contractYears.length - 1];
    this.setInitialPeriod(lastContract, lastPeriod);
  }

  /**
   *
   * @param contracts
   */
  private mapContracts(contracts: IdpContract[]): Contract[] {
    return contracts.map(contract => ({
      id: contract.id,
      displayNameContractWise: contract.displayNameContractWise,
      contractYears: contract.contractYears.map(this.mapPeriod)
    }));
  }

  /**
   *
   * @param period
   */
  private mapPeriod(period: IdpContract['contractYears'][number]): ContractPeriod {
    return {
      id: period.id,
      displayNameContractYearWise: period.displayNameContractYearWise,
      startDate: period.startDate,
      endDate: period.endDate,
      yearlyPageExtractionQuota: period.yearlyPageExtractionQuota
    };
  }

  /**
   *
   */
  private handleError(): void {
    this.state = {
      ...this.initialState,
      isMeterDataEmpty: true
    };
  }

  /**
   *
   */
  private initializeMenu(): void {
    this.data.menuItems = this.data.contracts.map(contract => ({
      label: contract.displayNameContractWise,
      items: contract.contractYears.map(period => ({
        label: period.displayNameContractYearWise,
        icon: period.id === this.data.selectedPeriod?.id ? 'fa-solid fa-circle-check' : undefined,
        class: period.id === this.data.selectedPeriod?.id ? 'selected-menu-item' : undefined,
        selected: period.id === this.data.selectedPeriod?.id,
        command: () => this.onSelectPeriod(period, contract.displayNameContractWise)
      }))
    }));
  }

  /**
   *
   * @param period
   * @param contractLabel
   */
  onSelectPeriod(period: ContractPeriod, contractLabel: string): void {
    if (!period) {
      return;
    }
    this.data.selectedPeriod = period;
    this.state.selectedContractRange = contractLabel;
    this.state.selectedPeriodLabel = period.displayNameContractYearWise;
    this.state.yearlyPageExtractionQuota = period.yearlyPageExtractionQuota;
    this.initializeMenu();
    this.loadAggregateData(period.startDate, period.endDate);
  }

  /**
   *
   * @param contract
   * @param period
   */
  private setInitialPeriod(contract: Contract, period: ContractPeriod): void {
    if (!period) {
      return;
    }
    this.data.selectedPeriod = period;
    this.state.selectedContractRange = contract.displayNameContractWise;
    this.state.selectedPeriodLabel = period.displayNameContractYearWise;
    this.state.yearlyPageExtractionQuota = period.yearlyPageExtractionQuota;
    this.initializeMenu();
    this.loadAggregateData(period.startDate, period.endDate);
  }

  /**
   *
   * @param startDate
   * @param endDate
   */
  private loadAggregateData(startDate: string, endDate: string): void {
    this.state.aggregateDataloading = true;

    this.idpMeteringAggregateService
      .getAggregateMeteringData(startDate, endDate)
      .pipe(
        retry({
          count: 2,
          delay: (error, retryCount) => {
            if (error.status === 0) {
              return of(1000);
            }
            throw error;
          },
          resetOnSuccess: true
        }),
        takeUntil(this.destroy$),
        catchError(error => {
          this.state.aggregateDataloading = false;
          this.state.pagesExtractedTotal = 0;
          return [];
        })
      )
      .subscribe({
        next: (data: IdpAggregateMeteringItem) => {
          this.data.aggregateData = Array.isArray(data) ? data[0] : data;
          this.state.pagesExtractedTotal = this.data.aggregateData?.pagesExtracted || 0;
          this.state.hasExceededQuota =
            this.state.pagesExtractedTotal > this.state.yearlyPageExtractionQuota;
          this.state.aggregateDataloading = false;
          this.state.meterGrouptotalPercent = this.getMeterGrouptotalPercent();
        }
      });
  }

  /**
   *
   */
  getMeterGrouptotalPercent(): number {
    const quota = this.state.yearlyPageExtractionQuota || 1;
    const extracted = this.state.pagesExtractedTotal;
    return Math.min(100, Math.ceil((extracted / quota) * 100));
  }

  /**
   *
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
